import { VestigingFormContext } from "../../../../Vestigingen/VestigingForm";
import { useFormContext, useWatch } from "react-hook-form";
import { Alert, Card, Chip, Collapse, Grid, InputAdornment, MenuItem } from "@mui/material";
import CardBody from "../../../../../components/Card/CardBody";
import FormTextTC from "../../../../../components/Forms/FormTextTC";
import TranslationWrapper, { getTranslation } from "../../../../../components/TranslationWrapper/TranslationWrapper";
import { FormGroupWithInlineButtonsTC } from "../../../../../components/Forms/FormGroupWithInlineButtonsTC";
import FormText from "../../../../../components/Forms/FormText";
import { InputValidation } from "../../../../../components/Forms/InputValidation";
import FormSelectTC from "../../../../../components/Forms/FormSelectTC";
import FormSwitchTC from "../../../../../components/Forms/FormSwitchTC";
import { CustomizableTexts } from "../../../../../components/CustomizableTexts/CustomizableTexts";
import AsyncProductAutocompleteTC from "../../../../../components/Forms/AsyncAutocomplete/Product/AsyncProductAutocompleteTC";
import React from "react";
import { ArrangementVariant } from "../General";
import { ContextualValue } from "../../../../../components/Forms/ContextualValue";

export function RegularArrangement({ variant }: { variant: ArrangementVariant }) {
  const { menukaartVestigingId, vestigingId, setValue }: VestigingFormContext = useFormContext();
  const visit_length: "duration" | "schedule" | "no_time" = useWatch({ name: "visit_length" });
  let max_rounds: number | string = useWatch({ name: "max_rounds" });
  if (typeof max_rounds === "string") {
    max_rounds = Number.parseInt(max_rounds);
  }

  let minutes_between_rounds: number | string = useWatch({ name: "minutes_between_every_round" });
  if (typeof minutes_between_rounds === "string") {
    minutes_between_rounds = Number.parseInt(minutes_between_rounds);
  }

  let credits: number | string = useWatch({ name: "max_number_of_items_per_round_per_cover" });
  if (typeof credits === "string") {
    credits = Number.parseInt(credits);
  }

  const almost_done_reminder_enabled = useWatch({ name: "almost_done_reminder_enabled" });
  const extend_visit_enabled = useWatch({ name: "allow_extending_visits" });

  const extra_rounds_disabled = max_rounds === 0;
  const extra_time_disabled = visit_length === "no_time";
  return (
    <Collapse in={variant === "regular"} mountOnEnter unmountOnExit>
      <Card>
        <CardBody style={{ padding: 16 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormTextTC name={"name"} tDefault={"Name"} tKey={"arrangements.name"} />
              <FormGroupWithInlineButtonsTC
                formField={
                  <FormText
                    fieldProps={{
                      InputProps: {
                        endAdornment: <InputAdornment position={"end"}>Credits</InputAdornment>,
                        ...(credits === 0
                          ? {
                              startAdornment: (
                                <InputAdornment position={"start"}>
                                  <Chip
                                    label={
                                      <TranslationWrapper
                                        tDefault={"No credit limit per round"}
                                        tKey={"ayce.form.max_credits.no_limit"}
                                      />
                                    }
                                  />
                                </InputAdornment>
                              ),
                            }
                          : {}),
                      },
                    }}
                    name={"max_number_of_items_per_round_per_cover"}
                    rules={{
                      min: {
                        value: 0,
                        message: getTranslation("The value must not be negative"),
                      },
                    }}
                  />
                }
                tDefault={"Max number of credits per round per cover"}
                tKey={"arrangement-rules-collection.max_number_of_items_per_round_per_cover"}
              >
                <ContextualValue targetValue={5}>
                  <TranslationWrapper tDefault={"Default"} tKey={"Default"} />
                </ContextualValue>
                <ContextualValue targetValue={0}>
                  <TranslationWrapper tDefault={"No limit"} tKey={"ayce.form.credits.helper-button.no-limit"} />
                </ContextualValue>
              </FormGroupWithInlineButtonsTC>
              <FormGroupWithInlineButtonsTC
                formField={
                  <FormText
                    fieldProps={{
                      InputProps: {
                        endAdornment: <InputAdornment position={"end"}>Rounds</InputAdornment>,
                        ...(max_rounds === 0
                          ? {
                              startAdornment: (
                                <InputAdornment position={"start"}>
                                  <Chip
                                    label={
                                      <TranslationWrapper
                                        tDefault={"No round limit"}
                                        tKey={"ayce.form.max_rounds.no_limit"}
                                      />
                                    }
                                  />
                                </InputAdornment>
                              ),
                            }
                          : {}),
                      },
                    }}
                    name={"max_rounds"}
                    rules={{
                      ...InputValidation.REQUIRED,
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: getTranslation("The value must not be negative"),
                      },
                      pattern: {
                        ...InputValidation.REGEX_VALIDATE_WHOLE_NUMBER,
                      },
                    }}
                  />
                }
                tDefault={"Max number of rounds"}
                tKey={"arrangement-rules-collection.max_rounds"}
              >
                <ContextualValue targetValue={5}>
                  <TranslationWrapper tDefault={"Default"} tKey={"Default"} />
                </ContextualValue>
                <ContextualValue targetValue={0}>
                  <TranslationWrapper tDefault={"No limit"} tKey={"ayce.form.max-rounds.helper-button.no-limit"} />
                </ContextualValue>
              </FormGroupWithInlineButtonsTC>
              <FormGroupWithInlineButtonsTC
                formField={
                  <FormText
                    fieldProps={{
                      InputProps: {
                        endAdornment: <InputAdornment position={"end"}>Minutes</InputAdornment>,
                        ...(minutes_between_rounds === 0
                          ? {
                              startAdornment: (
                                <InputAdornment position={"start"}>
                                  <Chip
                                    label={
                                      <TranslationWrapper
                                        tDefault={"No waiting time"}
                                        tKey={"ayce.form.minutes-between-rounds.helper-button.no_timer"}
                                      />
                                    }
                                  />
                                </InputAdornment>
                              ),
                            }
                          : {}),
                      },
                    }}
                    name={"minutes_between_every_round"}
                    rules={{
                      required: true,
                      min: {
                        value: 0,
                        message: getTranslation("The value must not be negative"),
                      },
                    }}
                  />
                }
                tDefault={"Number of minutes between every round"}
                tKey={"arrangement-rules-collection.minutes_between_every_round"}
              >
                <ContextualValue targetValue={10}>
                  <TranslationWrapper tDefault={"Default"} tKey={"Default"} />
                </ContextualValue>
                <ContextualValue targetValue={0}>
                  <TranslationWrapper
                    tDefault={"No waiting time"}
                    tKey={"ayce.form.minutes-between-rounds.helper-button.no_timer"}
                  />
                </ContextualValue>
              </FormGroupWithInlineButtonsTC>
            </Grid>
          </Grid>
        </CardBody>
      </Card>
      <Card sx={{ mt: 2 }}>
        <CardBody>
          <h4>Visit duration</h4>
          <FormSelectTC
            defaultValue={"no_time"}
            name={"visit_length"}
            options={[
              <MenuItem value={"duration"}>A duration (For example, 2 hours)</MenuItem>,
              // not implemented yet
              // <MenuItem value="schedule">A time schedule</MenuItem>,
              <MenuItem value={"no_time"}>As long as the customer wants (no time limit)</MenuItem>,
            ]}
            tDefault={"Visit length"}
            tKey={"arrangement.visit-duration.duration-or-schedule"}
          />
          <Collapse in={visit_length === "duration"} mountOnEnter unmountOnExit>
            <FormTextTC
              fieldProps={{
                type: "number",
                InputProps: {
                  endAdornment: <InputAdornment position={"end"}>minutes</InputAdornment>,
                },
              }}
              name={"max_time_in_minutes"}
              rules={{
                required: true,
                min: {
                  value: 1,
                  message: getTranslation(
                    "arrangement.visit-duration.time-in-minutes.wrong-value",
                    "A duration cannot be negative"
                  ),
                },
                max: {
                  // max value in milliseconds for setTimeout
                  value: Math.floor((2 ** 31 - 1) / 60_000),
                  message: getTranslation(
                    `The value must not be bigger than ${Math.floor((2 ** 31 - 1) / 60_000)} minutes (24 days)`
                  ),
                },
              }}
              tDefault={"Visit duration in minutes"}
              tKey={"arrangement.visit-duration.time-in-minutes"}
            />
            <h5 style={{ marginTop: "24px", marginBottom: "8px" }}>Eating time almost over notification</h5>
            <p>
              The customer can be notified with a reminder when their arrangement experience is about to end.
              <br />A notification can be shown 15 minutes before their time has ran out.
            </p>
            <FormSwitchTC
              name={"almost_done_reminder_enabled"}
              tDefault={"'Almost finished' reminder enabled"}
              tKey={"ayce.form.almost-finished-reminder.enabled-value"}
            />
            <Collapse in={almost_done_reminder_enabled} mountOnEnter unmountOnExit>
              <CustomizableTexts
                translatableTexts={["Almost finished reminder - title", "Almost finished reminder - body"]}
                vestigingId={vestigingId!}
              />
            </Collapse>
          </Collapse>
        </CardBody>
      </Card>
      <Card sx={{ mt: 2 }}>
        <CardBody>
          <h4>Extending visits</h4>
          <p>
            The max number of rounds and the visit length is usually the same for every visitor. Once the maximum amount
            of rounds or the timer is reached, the visit is over for the customer and they have to pay.
          </p>
          <p>
            You can configure a product to extend a visitor's amount of rounds or visit time below, a waiter is allowed
            to add this product to a table. You can choose whether you want to charge for this service by setting a
            price on the product, or by setting it to 0.
          </p>
          <FormSwitchTC
            name={"allow_extending_visits"}
            tDefault={"Enable extending visits"}
            tKey={"ayce.form.extending-visits.enable"}
          />
          <Collapse in={extend_visit_enabled} mountOnEnter unmountOnExit>
            <FormSelectTC
              name={"visit_extension_calculation_method"}
              options={[
                <MenuItem disabled>
                  <span>
                    For this option, add one or more extension products <u>per guest</u> at the table.
                  </span>
                </MenuItem>,
                <MenuItem value={"per_couvert"}>Charge per couvert</MenuItem>,
                <MenuItem disabled>For this option, add one or more extension products.</MenuItem>,
                <MenuItem value={"per_table"}>Charge per table</MenuItem>,
              ]}
              tDefault={"How do you want to charge for this service?"}
              tKey={"ayce.form.extending-visits.charge_per_table_or_per_couvert"}
              tooltip={
                <>
                  <p>
                    Whether you charge per table or per couvert affects how many products you need to add to a table for
                    the desired time or rounds extension.
                  </p>
                  <p>
                    If you configure it as per table, you add one product per extension. When configured as per couvert,
                    you need to add one product for each person at the table.
                  </p>
                </>
              }
            />
            <Collapse in={extra_rounds_disabled} mountOnEnter unmountOnExit>
              <Alert severity={"info"}>
                <TranslationWrapper
                  tDefault={"Rounds are set to no limit; adding extra rounds is disabled"}
                  tKey={"ayce.form.extending-visits.no-rounds-alert"}
                />
              </Alert>
            </Collapse>
            <h5 style={{ fontSize: "1.2em" }}>Extra rounds</h5>
            <AsyncProductAutocompleteTC
              async_url={`core/producten/search?fields=id as value, naam as label,id,naam,snaam,blocked,is_actief,prijs&per_page=50&sort_field=naam&vestiging_id=${menukaartVestigingId}`}
              autocompleteProps={{
                disabled: extra_rounds_disabled,
              }}
              name={"round_extension_product_id"}
              tDefault={"The extra rounds product"}
              tKey={"ayce.form.extending-visits.extra-rounds-product"}
            />

            <h5 style={{ fontSize: "1.2em" }}>Extra time</h5>
            <Collapse in={extra_time_disabled} mountOnEnter unmountOnExit>
              <Alert severity={"info"}>
                <TranslationWrapper
                  tDefault={"Visit length is set to unlimited; adding extra time is disabled"}
                  tKey={"ayce.form.extending-visits.no-time-alert"}
                />
              </Alert>
            </Collapse>
            <AsyncProductAutocompleteTC
              async_url={`core/producten/search?fields=id as value, naam as label,id,naam,snaam,blocked,is_actief,prijs&per_page=50&sort_field=naam&vestiging_id=${menukaartVestigingId}`}
              autocompleteProps={{
                disabled: extra_time_disabled,
              }}
              name={"time_extension_product_id"}
              tDefault={"The extra time product"}
              tKey={"ayce.form.extending-visits.extra-time-product"}
            />
            <FormTextTC
              fieldProps={{
                defaultValue: 15,
                disabled: extra_time_disabled,
                InputProps: {
                  endAdornment: <InputAdornment position={"end"}>Minutes</InputAdornment>,
                },
              }}
              name={"minutes_added_per_time_extension"}
              rules={{
                min: {
                  value: 1,
                  message: getTranslation("The value must not be negative"),
                },
              }}
              tDefault={"Minutes added per extension product"}
              tKey={"ayce.form.extending-visits.minutes-per-extension"}
            />
          </Collapse>
        </CardBody>
      </Card>
    </Collapse>
  );
}
